import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

gsap.registerPlugin(ScrollTrigger, SplitText)

// Reveal-on-scroll: drop-in replacement for the old IntersectionObserver
// version — same [data-reveal] / [data-reveal-stagger] attribute contract
// and the same html.js-anim / .is-visible CSS contract from _polish.scss,
// so no template markup or CSS changes are needed. The opacity/transform
// transition — and the prefers-reduced-motion fallback — stay CSS-driven;
// this only swaps the trigger mechanism.
function initReveal() {
	document.querySelectorAll('[data-reveal-stagger]').forEach((group) => {
		const step = parseInt(group.getAttribute('data-reveal-stagger'), 10) || 90
		const kids = group.querySelectorAll(':scope > [data-reveal], :scope > * > [data-reveal]')
		kids.forEach((el, i) => el.style.setProperty('--reveal-delay', `${i * step}ms`))
	})

	const items = gsap.utils.toArray('[data-reveal]')
	if (!items.length) return

	ScrollTrigger.batch(items, {
		start: 'top 92%',
		once: true,
		onEnter: (batch) => batch.forEach((el) => el.classList.add('is-visible')),
	})

	// Vangnet: nooit verborgen blijven (verborgen tab / headless renderer)
	window.setTimeout(() => items.forEach((el) => el.classList.add('is-visible')), 2200)
}

// Cinematische hero-intro: koptekst valt letter voor letter in — tanigro's
// meest kenmerkende motion-patroon. Alleen de losse letter-spans worden
// geanimeerd; de zichtbaarheid van de kop zelf blijft in handen van het
// bestaande data-reveal-systeem hierboven.
// LET OP: type moet "chars, words" zijn (niet enkel "chars") — anders wordt
// elk teken een losstaand inline-block-element zonder woordgrenzen, waardoor
// de regel midden in een woord kan afbreken (bv. "experti" / "se").
function initHeroSplit() {
	gsap.utils.toArray('.row-type-hero h1, .row-type-crop-hero h1').forEach((heading) => {
		const split = new SplitText(heading, { type: 'chars, words' })
		gsap.from(split.chars, {
			autoAlpha: 0,
			y: '0.6em',
			stagger: 0.018,
			duration: 0.8,
			ease: 'power3.out',
			delay: 0.15,
		})
	})
}

// Woord-voor-woord reveal op sectiekoppen en het citaat — lichter dan de
// letter-split van de hero, gecombineerd met de bestaande data-reveal fade-up
// van de omliggende .section-head/.row-type-quote container.
function initTextReveal() {
	const allWords = []

	gsap.utils.toArray('.section-head__title, .row-type-quote__text').forEach((el) => {
		const split = new SplitText(el, { type: 'words' })
		gsap.set(split.words, { autoAlpha: 0, y: '0.3em' })
		allWords.push(...split.words)

		ScrollTrigger.create({
			trigger: el,
			start: 'top 88%',
			once: true,
			onEnter: () => gsap.to(split.words, {
				autoAlpha: 1,
				y: 0,
				stagger: 0.03,
				duration: 0.6,
				ease: 'power2.out',
			}),
		})
	})

	// Zelfde vangnet-principe als [data-reveal]: nooit permanent onzichtbaar
	// blijven als ScrollTrigger om wat voor reden dan ook niet vuurt.
	window.setTimeout(() => gsap.to(allWords, { autoAlpha: 1, y: 0, duration: 0.3 }), 2500)
}

// Oneindig scrollende gewassen-ticker — dubbele track, naadloze loop via
// xPercent -50 (de tweede track staat exact waar de eerste begon).
function initMarquee() {
	document.querySelectorAll('.row-type-marquee .marquee').forEach((marquee) => {
		const trackWidth = marquee.scrollWidth / 2
		const pxPerSecond = 45
		const tween = gsap.to(marquee, {
			xPercent: -50,
			duration: trackWidth / pxPerSecond,
			ease: 'none',
			repeat: -1,
		})

		const section = marquee.closest('.row-type-marquee')
		section.addEventListener('mouseenter', () => tween.pause())
		section.addEventListener('mouseleave', () => tween.play())
	})
}

// Subtiele parallax op grote beelden: ScrollTrigger.scrub koppelt de
// verticale verschuiving direct aan de scrollpositie (geen aparte tween).
// .row-type-hero__img heeft bewust 130% hoogte (zie _custom.scss) zodat
// ±6% nooit een rand van de foto laat zien.
function initParallax() {
	gsap.utils.toArray('.row-type-hero__img, .parallax-image').forEach((img) => {
		gsap.fromTo(img,
			{ yPercent: -6 },
			{
				yPercent: 6,
				ease: 'none',
				scrollTrigger: {
					trigger: img.closest('.row-type-hero, .parallax-image-wrapper') || img.parentElement,
					start: 'top bottom',
					end: 'bottom top',
					scrub: true,
				},
			}
		)
	})
}

// Knop-tekst-slide: wrapt de tekst van elke .btn in een duplicaat-structuur
// zodat de CSS-hover (.btn-text-slide__line, zie _polish.scss) een identieke
// kopie van onderaf kan laten opschuiven — tanigro's enige knop-hover-effect.
// Puur progressive enhancement: zonder JS blijft de knop gewoon leesbare tekst.
function initButtonTextSlide() {
	document.querySelectorAll('.btn').forEach((btn) => {
		if (btn.tagName === 'INPUT') return
		if (btn.querySelector(':scope > .btn-text-slide')) return

		const label = btn.textContent.trim()
		if (!label) return

		const html = btn.innerHTML
		btn.innerHTML = `<span class="btn-text-slide"><span class="btn-text-slide__line">${html}</span><span class="btn-text-slide__line" aria-hidden="true">${html}</span></span>`
	})
}

function initStickyHeader() {
	const root = document.documentElement
	const update = () => root.classList.toggle('is-scrolled', window.scrollY > 24)
	update()
	window.addEventListener('scroll', update, { passive: true })
}

function init() {
	initReveal()
	initStickyHeader()
	initButtonTextSlide()

	// SplitText/parallax/marquee hebben geen CSS-vangnet zoals [data-reveal] —
	// bij reduced motion slaan we ze over en blijft alles leesbaar/statisch
	// (de dubbele marquee-track wordt in dat geval via CSS verborgen).
	gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
		initHeroSplit()
		initTextReveal()
		initParallax()
		initMarquee()
	})
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init)
} else {
	init()
}
